/**
 * Shared motion vocabulary — the single source of truth for animation
 * across the site. Ported from the app's motion tokens
 * (muse-app/src/utils/theme.ts `animation`): springs target 120–400ms
 * perceived motion, scales stay in the 0.94–1.06 band so nothing pops
 * layout, and the delight lives in mechanics, not decoration.
 */
import type { Transition, Variants } from 'framer-motion';

/** Signature ease — shared with the gallery's cascade reveals. */
export const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const duration = {
  instant: 0.1,
  fast: 0.2,
  normal: 0.35,
  slow: 0.5,
  entrance: 0.45,
} as const;

export const stagger = {
  fast: 0.04,
  normal: 0.08,
  slow: 0.12,
} as const;

export const spring: Record<string, Transition> = {
  gentle: { type: 'spring', mass: 0.5, damping: 14, stiffness: 130 },
  snappy: { type: 'spring', mass: 0.3, damping: 18, stiffness: 280 },
  bouncy: { type: 'spring', mass: 0.4, damping: 9, stiffness: 160 },
  slow: { type: 'spring', mass: 0.7, damping: 18, stiffness: 90 },
  /** Press-down — critically damped, no overshoot, ~120ms */
  press: { type: 'spring', mass: 0.25, damping: 22, stiffness: 380 },
  /** Press-release — tiny overshoot (~3%), ~200ms */
  release: { type: 'spring', mass: 0.4, damping: 14, stiffness: 220 },
  /** Celebrate — joyful overshoot for milestone moments */
  celebrate: { type: 'spring', mass: 0.35, damping: 8, stiffness: 180 },
};

export const scale = {
  pressSubtle: 0.98,
  press: 0.94,
  pressStrong: 0.9,
  rest: 1,
} as const;

/** Standard once-only viewport trigger for scroll reveals. */
export const viewportOnce = { once: true, margin: '-80px' } as const;

/** Quiet rise — the default section/content entrance. */
export const fadeRise: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.entrance, ease: EASE },
  },
};

/**
 * Line-mask reveal — pair with an `overflow: hidden` wrapper.
 * The inner line rises from below its own baseline like print
 * coming off the press.
 */
export const maskRevealLine: Variants = {
  hidden: { y: '110%' },
  visible: {
    y: 0,
    transition: { duration: duration.slow + 0.15, ease: EASE },
  },
};

/** Hairline rule drawing in from the left. */
export const ruleDraw: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: duration.slow + 0.2, ease: EASE },
  },
};

/** Vertical spine drawing downward (process ledgers, welcome chapter). */
export const ruleDrawVertical: Variants = {
  hidden: { scaleY: 0 },
  visible: {
    scaleY: 1,
    transition: { duration: duration.slow + 0.2, ease: EASE },
  },
};

/** Container that staggers its children's `hidden`→`visible` variants. */
export const staggerChildren = (interval: number = stagger.normal): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: interval } },
});
